import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Target,
  Star,
  Upload,
  FileText,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Edit,
  Send,
  Download,
  Plus,
  MessageSquare,
  Lock,
  PlayCircle,
  X,
  Eye,
  TrendingUp,
  Trash2,
  ChevronDown,
  Award,
  RefreshCw,
  ArrowLeft,
  ArrowRight
} from 'lucide-react';
import { formatDate } from '../../utils/stringUtils';
import { useAuth } from '../../context/AuthContext';
import { goalApi } from '../../api/goalApi';
import { reviewApi } from '../../api/reviewApi';
import { outcomeApi } from '../../api/outcomeApi';
import { toast } from 'react-hot-toast';
import { usePerformanceCycles } from '../../hooks/usePerformanceCycles';
import essService from '../../api/services/essService';
import Button from '../../components/common/Button/Button';
import PaginationControls from '../../components/common/Pagination/PaginationControls';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  hr_manager: 'HR Manager',
  hr_executive: 'HR Executive',
  employee: 'Employee',
  customer: 'Customer',
  sales_manager: 'Sales Manager',
  sales_executive: 'Sales Executive',
  business_relationship_manager: 'Business Relationship Manager',
  business_development_executive: 'Business Development Executive',
  dispatch_manager: 'Dispatch Manager',
  support_manager: 'Support Manager'
};

const formatRoleLabel = (roleValue) => {
  if (!roleValue) return roleValue;
  const normalized = String(roleValue).trim().toLowerCase();
  if (ROLE_LABELS[normalized]) return ROLE_LABELS[normalized];
  return String(roleValue)
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word ? word.charAt(0).toUpperCase() + word.slice(1) : '')
    .join(' ')
    .trim();
};

const MyPerformance = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('goals');
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [viewingGoal, setViewingGoal] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [selectedGoalForEvidence, setSelectedGoalForEvidence] = useState(null);
  const [viewingReview, setViewingReview] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [viewingOutcome, setViewingOutcome] = useState(null);
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCycle, setFilterCycle] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchInputRef = useRef(null);

  // Pagination state
  const [goalsCurrentPage, setGoalsCurrentPage] = useState(1);
  const [goalsItemsPerPage, setGoalsItemsPerPage] = useState(10);
  const [goalsTotalPages, setGoalsTotalPages] = useState(1);
  const [goalsTotal, setGoalsTotal] = useState(0);

  const [reviewsCurrentPage, setReviewsCurrentPage] = useState(1);
  const [reviewsItemsPerPage, setReviewsItemsPerPage] = useState(10);
  const [reviewsTotalPages, setReviewsTotalPages] = useState(1);
  const [reviewsTotal, setReviewsTotal] = useState(0);

  const [outcomesCurrentPage, setOutcomesCurrentPage] = useState(1);
  const [outcomesItemsPerPage, setOutcomesItemsPerPage] = useState(10);
  const [outcomesTotalPages, setOutcomesTotalPages] = useState(1);
  const [outcomesTotal, setOutcomesTotal] = useState(0);

  // Get employee ID from user (this might be a string like "EMP001" or ObjectId)
  const userEmployeeId = user?.employeeId || user?._id || user?.id || user?.employee?._id || null;

  // Fetch employee profile to get the correct MongoDB ObjectId for filtering
  const {
    data: employeeProfile,
    isLoading: profileLoading
  } = useQuery({
    queryKey: ['employeeProfile'],
    queryFn: async () => {
      try {
        const result = await essService.getProfile();
        if (result.success && result.data) {
          return result.data;
        }
        return null;
      } catch (error) {
        return null;
      }
    },
    enabled: !!userEmployeeId,
    staleTime: 0,
  });

  // Get the correct employee ObjectId for filtering
  // Use the Employee document's _id (MongoDB ObjectId) which is what goals are stored with
  const employeeId = employeeProfile?._id || employeeProfile?.id || userEmployeeId;

  // Debug: Log employeeId to check if it's being set correctly
  React.useEffect(() => {
    if (employeeId) {
    }
  }, [employeeId, employeeProfile, user]);

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch all performance cycles (needed for historical goal references)
  const cycleFilters = useMemo(() => ({}), []);

  const {
    data: cyclesResponse,
    isLoading: cyclesLoading
  } = usePerformanceCycles(cycleFilters);

  const cycles = useMemo(() => {
    if (cyclesResponse?.data?.cycles) {
      return cyclesResponse.data.cycles;
    } else if (Array.isArray(cyclesResponse?.cycles)) {
      return cyclesResponse.cycles;
    } else if (Array.isArray(cyclesResponse?.data)) {
      return cyclesResponse.data;
    } else if (Array.isArray(cyclesResponse)) {
      return cyclesResponse;
    }
    return [];
  }, [cyclesResponse]);

  // Get current active cycle (most recent launched cycle)
  const currentCycle = useMemo(() => {
    if (!cycles || cycles.length === 0) return null;
    const activeCycles = cycles.filter(c => c.status === 'launched' || c.status === 'draft');
    if (activeCycles.length === 0) return null;
    // Sort by startDate descending and get the most recent
    const sorted = [...activeCycles].sort((a, b) => {
      const dateA = new Date(a.startDate || a.createdAt || 0);
      const dateB = new Date(b.startDate || b.createdAt || 0);
      return dateB - dateA;
    });
    return sorted[0];
  }, [cycles]);

  // Get employee name from profile or user for filtering
  const employeeName = employeeProfile?.personalInfo?.firstName && employeeProfile?.personalInfo?.lastName
    ? `${employeeProfile.personalInfo.firstName} ${employeeProfile.personalInfo.lastName}`.trim()
    : (user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`.trim()
      : user?.name || user?.fullName || employeeProfile?.name || null);

  // Goal filters
  const goalFilters = useMemo(() => {
    const filters = {
      // Try employeeId first (MongoDB ObjectId), fallback to employee name
      ...(employeeId ? { employeeId } : {}),
      ...(employeeName && !employeeId ? { employee: employeeName } : {}),
      status: filterStatus !== 'all' ? filterStatus : undefined,
      cycle: filterCycle !== 'all' ? filterCycle : undefined,
      q: debouncedSearch?.trim() || undefined,
      page: goalsCurrentPage,
      limit: goalsItemsPerPage
    };
    // Remove undefined values
    Object.keys(filters).forEach(key => filters[key] === undefined && delete filters[key]);
    return filters;
  }, [employeeId, employeeName, filterStatus, filterCycle, debouncedSearch, goalsCurrentPage]);

  // Fetch employee goals
  const {
    data: goalsResponse,
    isLoading: goalsLoading,
    refetch: refetchGoals
  } = useQuery({
    queryKey: ['employeeGoals', goalFilters],
    queryFn: async () => {
      if (!employeeId) {
        return { data: [], pagination: { total: 0, totalPages: 1 } };
      }

      // Use getGoals with filters (same as GoalManagement.jsx)
      const response = await goalApi.getGoals(goalFilters);

      // Handle pagination response
      if (response?.data) {
        // New structure: { data: [...], pagination: {...} }
        if (response.data.data && response.data.pagination) {
          return {
            data: Array.isArray(response.data.data) ? response.data.data : [],
            pagination: response.data.pagination
          };
        }
        // Old structure: just array or { data: [...] }
        return {
          data: Array.isArray(response.data) ? response.data : (response.data.data || []),
          pagination: response.pagination || response.data.pagination || { total: Array.isArray(response.data) ? response.data.length : 0, totalPages: 1 }
        };
      }

      // Fallback for array response
      const goalsArray = Array.isArray(response) ? response : [];
      return {
        data: goalsArray,
        pagination: { total: goalsArray.length, totalPages: 1 }
      };
    },
    enabled: !!employeeId && !profileLoading && activeTab === 'goals',
    staleTime: 0
  });

  // Extract goals and pagination from response
  const goals = useMemo(() => {
    const result = goalsResponse?.data || [];

    // Update pagination state
    if (goalsResponse?.pagination) {
      setGoalsTotalPages(goalsResponse.pagination.totalPages || 1);
      setGoalsTotal(goalsResponse.pagination.total || result.length);
    }

    return Array.isArray(result) ? result : [];
  }, [goalsResponse]);

  // Review filters - filter by employeeId to show only reviews assigned to this employee
  const reviewFilters = useMemo(() => {
    const filters = {
      reviewType: 'self', // Show self reviews for the employee
      employeeId: employeeId, // Filter by employeeId (MongoDB ObjectId) to show only this employee's reviews
      status: undefined,
      cycle: undefined,
      search: debouncedSearch?.trim() || undefined,
      page: reviewsCurrentPage,
      limit: reviewsItemsPerPage
    };
    // Remove undefined values
    Object.keys(filters).forEach(key => filters[key] === undefined && delete filters[key]);
    return filters;
  }, [employeeId, debouncedSearch, reviewsCurrentPage]);

  // Fetch employee reviews
  const {
    data: reviewsResponse,
    isLoading: reviewsLoading,
    refetch: refetchReviews
  } = useQuery({
    queryKey: ['employeeReviews', reviewFilters],
    queryFn: async () => {
      if (!employeeId) {
        return { data: [], pagination: { total: 0, totalPages: 1 } };
      }

      // Use getReviews with filters (same as ReviewManagement.jsx)
      const response = await reviewApi.getReviews(reviewFilters);

      // Handle different response formats with pagination
      let reviewsData = [];
      let pagination = { total: 0, totalPages: 1 };

      // New structure: { data: { data: [...], pagination: {...} } }
      if (response?.data?.data && response.data.pagination) {
        reviewsData = Array.isArray(response.data.data) ? response.data.data : [];
        pagination = response.data.pagination;
      } else if (response?.data?.data) {
        reviewsData = Array.isArray(response.data.data) ? response.data.data : [];
        pagination = response.data.pagination || response.pagination || { total: reviewsData.length, totalPages: 1 };
      } else if (response?.data) {
        reviewsData = Array.isArray(response.data) ? response.data : [];
        pagination = response.pagination || { total: reviewsData.length, totalPages: 1 };
      } else if (Array.isArray(response)) {
        reviewsData = response;
        pagination = { total: reviewsData.length, totalPages: 1 };
      }

      return { data: reviewsData, pagination };
    },
    enabled: !!employeeId && !profileLoading && activeTab === 'reviews',
    staleTime: 0
  });

  // Extract reviews from response (handle different response formats)
  const reviews = useMemo(() => {
    const result = reviewsResponse?.data || [];

    // Update pagination state
    if (reviewsResponse?.pagination) {
      setReviewsTotalPages(reviewsResponse.pagination.totalPages || 1);
      setReviewsTotal(reviewsResponse.pagination.total || result.length);
    }

    return Array.isArray(result) ? result : [];
  }, [reviewsResponse]);

  // Fetch employee outcomes
  const outcomeFilters = useMemo(() => {
    const filters = {
      employeeId: employeeId, // Use MongoDB ObjectId
      page: outcomesCurrentPage,
      limit: outcomesItemsPerPage
    };
    Object.keys(filters).forEach(key => filters[key] === undefined && delete filters[key]);
    return filters;
  }, [employeeId, outcomesCurrentPage]);

  const {
    data: outcomesResponse,
    isLoading: outcomesLoading,
    refetch: refetchOutcomes
  } = useQuery({
    queryKey: ['employeeOutcomes', outcomeFilters],
    queryFn: async () => {
      if (!employeeId) {
        return { data: [], pagination: { total: 0, totalPages: 1 } };
      }

      const response = await outcomeApi.getOutcomes(outcomeFilters);

      // Handle different response formats with pagination
      let outcomesData = [];
      let pagination = { total: 0, totalPages: 1 };

      // New structure: { data: { data: [...], pagination: {...} } }
      if (response?.data?.data && response.data.pagination) {
        outcomesData = Array.isArray(response.data.data) ? response.data.data : [];
        pagination = response.data.pagination;
      } else if (response?.data?.data) {
        outcomesData = Array.isArray(response.data.data) ? response.data.data : [];
        pagination = response.data.pagination || response.pagination || { total: outcomesData.length, totalPages: 1 };
      } else if (response?.data) {
        outcomesData = Array.isArray(response.data) ? response.data : [];
        pagination = response.pagination || { total: outcomesData.length, totalPages: 1 };
      } else if (Array.isArray(response)) {
        outcomesData = response;
        pagination = { total: outcomesData.length, totalPages: 1 };
      }

      return { data: outcomesData, pagination };
    },
    enabled: !!employeeId && !profileLoading && activeTab === 'outcomes',
    staleTime: 0
  });

  // Extract outcomes from response
  const outcomes = useMemo(() => {
    const result = outcomesResponse?.data || [];

    // Update pagination state
    if (outcomesResponse?.pagination) {
      setOutcomesTotalPages(outcomesResponse.pagination.totalPages || 1);
      setOutcomesTotal(outcomesResponse.pagination.total || result.length);
    }

    return Array.isArray(result) ? result : [];
  }, [outcomesResponse]);

  // Function to calculate status based on progress and endDate
  const calculateStatus = (progress, endDate) => {
    if (progress === 100) {
      return 'completed';
    }

    if (progress > 0) {
      return 'in_progress';
    }

    return 'not_started';
  };

  // Cycles options for filter
  const cycleOptions = Array.isArray(cycles)
    ? cycles.map((c) => ({ label: c.cycleName, value: c.id }))
    : [];

  // Reusable searchable select (small card within form)
  const SearchSelect = ({ label, value, onChange, options, placeholder = "Search or select", required = false, error = false, helperText, showDetails = false }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setOpen(false);
          setQuery("");
        }
      };

      if (open) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [open]);

    const normalized = Array.isArray(options)
      ? options.map((opt) => {
        if (typeof opt === 'string') {
          return { label: opt, displayLabel: opt };
        }
        const mainLabel = opt.label || opt.name || opt.fullName || "";
        const employeeId = opt.employeeId || opt.id || opt._id || "";
        const department = opt.department || "";
        const email = opt.email || "";

        return {
          label: mainLabel,
          displayLabel: mainLabel,
          employeeId: employeeId,
          department: department,
          email: email,
          ...opt
        };
      })
      : [];
    const filtered = normalized.filter((o) => {
      const searchText = query.toLowerCase();
      return o.label.toLowerCase().includes(searchText) ||
        (o.employeeId && o.employeeId.toLowerCase().includes(searchText)) ||
        (o.department && o.department.toLowerCase().includes(searchText)) ||
        (o.email && o.email.toLowerCase().includes(searchText));
    });
    return (
      <div className="relative" ref={dropdownRef}>
        {label ? (
          <label className="block text-sm font-medium text-gray-700">{label}</label>
        ) : null}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`mt-1 w-full border rounded-md p-2 flex items-center justify-between text-left ${error ? "border-red-500" : "border-gray-300"}`}
        >
          <span className={value ? "text-gray-900" : "text-gray-500"}>{value || placeholder}</span>
          <ChevronDown className="h-4 w-4 text-gray-500" />
        </button>
        {open && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
            <div className="p-2">
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                  const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                  setQuery(value);
                }}
                onKeyPress={(e) => {
                  // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                  if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                maxLength={100}
                placeholder={placeholder}
                className="w-full border border-gray-300 rounded-md p-2 text-sm"
                autoFocus
              />
            </div>
            <div className="max-h-56 overflow-y-auto">
              {filtered.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500">No results</div>
              )}
              {filtered.map((opt, idx) => (
                <button
                  key={`${opt.label}-${idx}`}
                  type="button"
                  onClick={() => {
                    onChange(opt.label);
                    setOpen(false);
                    setQuery("");
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{opt.label}</span>
                    {showDetails && (opt.employeeId || opt.department) && (
                      <span className="text-xs text-gray-500 mt-0.5">
                        {opt.employeeId && opt.department
                          ? `${opt.employeeId} - ${opt.department}`
                          : opt.employeeId || opt.department}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        {(helperText || (required && !value)) && (
          <p className={`mt-1 text-xs ${error ? "text-red-600" : "text-gray-500"}`}>{helperText || "Required"}</p>
        )}
      </div>
    );
  };

  // Get initial goal form state function
  const getInitialGoal = () => ({
    title: '',
    description: '',
    status: 'not_started',
    progress: 0,
    priority: 'medium',
    startDate: '',
    endDate: '',
    target: '',
    current: '',
    cycle: currentCycle?.id || '',
    remark: ''
  });

  const [newGoal, setNewGoal] = useState(getInitialGoal());
  const [formErrors, setFormErrors] = useState({});

  // Auto-calculate status when progress or endDate changes
  React.useEffect(() => {
    const calculatedStatus = calculateStatus(newGoal.progress, newGoal.endDate);
    if (newGoal.status !== calculatedStatus) {
      setNewGoal(prev => ({ ...prev, status: calculatedStatus }));
    }
  }, [newGoal.progress, newGoal.endDate]);

  // Create goal mutation
  const createGoalMutation = useMutation({
    mutationFn: (goalData) => goalApi.createGoal(goalData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeGoals'] });
      toast.success('Goal created successfully!');
      setShowGoalModal(false);
      setNewGoal(getInitialGoal());
      setFormErrors({});
    },
    onError: (error) => {
      toast.error('Failed to create goal. Please try again.');
    }
  });

  // Update goal mutation
  const updateGoalMutation = useMutation({
    mutationFn: ({ goalId, goalData }) => goalApi.updateGoal(goalId, goalData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeGoals'] });
      toast.success('Goal updated successfully!');
      setShowGoalModal(false);
      setEditingGoal(null);
      setNewGoal(getInitialGoal());
      setFormErrors({});
    },
    onError: (error) => {
      toast.error('Failed to update goal. Please try again.');
    }
  });

  // Delete goal mutation
  const deleteGoalMutation = useMutation({
    mutationFn: (goalId) => goalApi.deleteGoal(goalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeGoals'] });
      toast.success('Goal deleted successfully!');
    },
    onError: (error) => {
      toast.error('Failed to delete goal. Please try again.');
    }
  });

  // Update goal progress mutation (only updates progress, not other fields)
  const updateProgressMutation = useMutation({
    mutationFn: ({ goalId, progress, remark }) => goalApi.updateGoalProgress(goalId, { progress, remark }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeGoals'] });
      toast.success('Progress updated successfully!');
      setShowGoalModal(false);
      setEditingGoal(null);
      setNewGoal(getInitialGoal());
      setFormErrors({});
    },
    onError: (error) => {
      toast.error('Failed to update progress. Please try again.');
    }
  });

  // Helper functions
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'not_started': return 'bg-gray-100 text-gray-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'submitted': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'in_progress': return <TrendingUp className="h-4 w-4" />;
      case 'not_started': return <Clock className="h-4 w-4" />;
      case 'approved': return <CheckCircle className="h-4 w-4" />;
      case 'rejected': return <XCircle className="h-4 w-4" />;
      case 'submitted': return <Send className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Handle text input key press
  const handleTextKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Handle number input key down
  const handleNumberKeyDown = (e) => {
    if (e.key === '-' || e.key === '.' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };

  // Validate goal form
  const validateGoalForm = () => {
    const errors = {};

    if (!newGoal.title?.trim()) {
      errors.title = 'Title is required';
    } else if (newGoal.title.length > 80) {
      errors.title = 'Title must be ≤ 80 characters';
    } else if (!/^[a-zA-Z0-9\s,]+$/.test(newGoal.title)) {
      errors.title = 'Title can only contain alphanumeric characters, spaces, and commas';
    } else if (newGoal.title.trim().length < 3) {
      errors.title = 'Title must be at least 3 characters';
    }

    if (!newGoal.description?.trim()) {
      errors.description = 'Description is required';
    } else if (newGoal.description.length > 200) {
      errors.description = 'Description must be ≤ 200 characters';
    } else if (!/^[a-zA-Z0-9\s,]+$/.test(newGoal.description)) {
      errors.description = 'Description can only contain alphanumeric characters, spaces, and commas';
    } else if (newGoal.description.trim().length < 5) {
      errors.description = 'Description must be at least 5 characters';
    }

    if (!newGoal.startDate) {
      errors.startDate = 'Start date is required';
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDate = new Date(newGoal.startDate);
      if (startDate < today) {
        errors.startDate = 'Start date cannot be in the past';
      }
    }

    if (!newGoal.endDate) {
      errors.endDate = 'End date is required';
    } else if (newGoal.startDate) {
      const startDate = new Date(newGoal.startDate);
      const endDate = new Date(newGoal.endDate);
      if (endDate < startDate) {
        errors.endDate = 'End date cannot be before start date';
      }
    }

    if (newGoal.remark && newGoal.remark.length > 1000) {
      errors.remark = 'Remark must be ≤ 1000 characters';
    }

    const progress = Number(newGoal.progress || 0);
    if (progress < 0 || progress > 100) {
      errors.progress = 'Progress must be between 0 and 100';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle create/update goal
  const handleSubmitGoal = async (e) => {
    e.preventDefault();

    if (!validateGoalForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    const remarkText = (newGoal.remark || '').trim();

    if (editingGoal) {
      // When editing from employee side, only update progress
      // The backend will automatically calculate status based on progress
      const progress = Number(newGoal.progress || 0);
      updateProgressMutation.mutate({
        goalId: editingGoal.id || editingGoal._id,
        progress,
        remark: remarkText || undefined
      });
      return;
    }

    // For creating new goals (if allowed in future)
    if (!currentCycle) {
      toast.error('No active performance cycle found');
      return;
    }

    // Recalculate status before submission
    const calculatedStatus = calculateStatus(newGoal.progress, newGoal.endDate);

    const goalData = {
      title: newGoal.title.trim(),
      description: newGoal.description.trim(),
      status: calculatedStatus,
      progress: Number(newGoal.progress || 0),
      priority: newGoal.priority || 'medium',
      startDate: newGoal.startDate,
      endDate: newGoal.endDate,
      target: newGoal.target || '0',
      current: newGoal.current || '0',
      cycle: currentCycle.id,
      employee: user?.name || user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
      employeeId: employeeId,
      employeeEmail: user?.email || '',
      manager: user?.manager || user?.managerName || 'System',
      managerId: user?.managerId || null,
      managerEmail: user?.managerEmail || '',
      remark: remarkText
    };

    createGoalMutation.mutate(goalData);
  };

  // Handle delete goal
  const handleDeleteGoal = async (goalId) => {
    const confirmed = await window.confirm('Are you sure you want to delete this goal? This action cannot be undone.');
    if (confirmed) {
      deleteGoalMutation.mutate(goalId);
    }
  };

  // Handle update progress
  const handleUpdateProgress = async (goalId, newProgress) => {
    if (newProgress < 0 || newProgress > 100) {
      toast.error('Progress must be between 0 and 100');
      return;
    }
    updateProgressMutation.mutate({ goalId, progress: newProgress });
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setFilterCycle('all');
    // Maintain focus after clearing
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 50);
  };

  // Handle edit goal (edit mode - only progress editable)
  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setViewingGoal(null);
    setIsViewMode(false);
    const endDate = goal.endDate ? new Date(goal.endDate).toISOString().split('T')[0] : '';
    const progress = goal.progress || 0;
    // Recalculate status based on current progress and endDate
    const calculatedStatus = calculateStatus(progress, endDate);

    setNewGoal({
      title: goal.title || '',
      description: goal.description || '',
      status: calculatedStatus,
      progress: progress,
      priority: goal.priority || 'medium',
      startDate: goal.startDate ? new Date(goal.startDate).toISOString().split('T')[0] : '',
      endDate: endDate,
      target: goal.target || '',
      current: goal.current || '',
      cycle: goal.cycleId || goal.cycle || currentCycle?.id || '',
      remark: goal.remark || ''
    });
    setShowGoalModal(true);
  };

  // Handle view goal (view-only mode)
  const handleViewGoal = (goal) => {
    setViewingGoal(goal);
    setEditingGoal(null);
    setIsViewMode(true);
    setShowGoalModal(true);
  };

  // Handle file upload (evidence)
  const handleFileUpload = async (e, goalId) => {
    const file = e.target.files[0];
    if (!file) return;

    // File size validation (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    // In a real implementation, you would upload the file and update the goal
    toast.info('File upload functionality will be implemented with backend support');
  };



  // Reset page to 1 when filters change
  useEffect(() => {
    setGoalsCurrentPage(1);
  }, [filterStatus, filterCycle, debouncedSearch]);

  useEffect(() => {
    setReviewsCurrentPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    setOutcomesCurrentPage(1);
  }, []);

  // Render Goals Tab
  const renderGoals = () => {
    if (profileLoading) {
      return (
        <div className="text-center py-12">
          <RefreshCw className="mx-auto h-12 w-12 text-gray-400 animate-spin" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Loading profile...</h3>
        </div>
      );
    }

    if (goalsLoading) {
      return (
        <div className="text-center py-12">
          <RefreshCw className="mx-auto h-12 w-12 text-gray-400 animate-spin" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Loading goals...</h3>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex flex-1 min-w-0">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search goals..."
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                  setSearchTerm(value);
                }}
                onKeyPress={(e) => {
                  if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                maxLength={100}
                autoFocus
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-[42px]"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 flex-shrink-0 h-[42px]"
            >
              <option value="all">All Status</option>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
            <div className="min-w-[240px] flex-shrink-0">
              <SearchSelect
                label=""
                value={
                  filterCycle === "all"
                    ? ""
                    : (cycleOptions.find((o) => o.value === filterCycle)?.label || "")
                }
                onChange={(val) => {
                  if (!val || val === "All Cycles") return setFilterCycle("all");
                  const match = cycleOptions.find((o) => o.label === val);
                  setFilterCycle(match?.value || "all");
                }}
                options={[{ label: "All Cycles" }, ...cycleOptions]}
                placeholder="All Cycles"
              />
            </div>
            {/* Clear Filters */}
            {(searchTerm || filterStatus !== 'all' || filterCycle !== 'all') && (
              <div className="relative flex-shrink-0">
                <Button
                  variant="outline"
                  icon={<X className="h-4 w-4" />}
                  onClick={clearFilters}
                  className="h-[42px]"
                >
                  Clear
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Goals List */}
        {goals.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Goals Found</h3>
            <p className="text-gray-600">You don't have any goals assigned yet. Please contact your manager or HR for goal assignment.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Goal</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {goals.map((goal) => (
                    <tr key={goal.id || goal._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="max-w-[320px]">
                          <div className="text-sm font-medium text-gray-900 capitalize truncate">{goal.title}</div>
                          <div className="text-sm text-gray-500 capitalize truncate">{goal.description}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(goal.status)}`}>
                          {getStatusIcon(goal.status)}
                          <span className="ml-1 capitalize">{goal.status?.replace('_', ' ')}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getPriorityColor(goal.priority)}`}>
                          {goal.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${goal.progress || 0}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-900">{goal.progress || 0}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {goal.endDate ? formatDate(goal.endDate, { includeTime: false }) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewGoal(goal)}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {goal.status !== 'completed' && (
                            <button
                              onClick={() => handleEditGoal(goal)}
                              className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                              title="Edit Progress"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {goalsTotal > goalsItemsPerPage && (
          <PaginationControls
            pagination={{
              currentPage: goalsCurrentPage,
              totalPages: goalsTotalPages,
              totalCount: goalsTotal,
              pageSize: goalsItemsPerPage
            }}
            onPageChange={(page) => {
              setGoalsCurrentPage(page);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onLimitChange={(limit) => {
              setGoalsItemsPerPage(limit);
              setGoalsCurrentPage(1);
            }}
          />
        )}
      </div>
    );
  };

  // Function to get cycle name by ID with fallback
  const getCycleName = (cycleId, fallbackName = null) => {
    // If we have a fallback name and no cycleId, use the fallback
    if (!cycleId && fallbackName) {
      return fallbackName;
    }

    // Convert cycleId to string
    const cycleIdStr = cycleId ? String(cycleId).trim() : null;

    // Check if cycleId looks like a MongoDB ObjectId (24 hex characters)
    const isObjectId = cycleIdStr && /^[0-9a-fA-F]{24}$/.test(cycleIdStr);

    // If it doesn't look like an ObjectId, it might be a cycle name string
    if (cycleIdStr && !isObjectId && cycleIdStr.length > 0) {
      // If it's not an ObjectId and has a reasonable length, it might be a name
      // But only return it if it doesn't look like an ID (has spaces, special chars, etc.)
      if (cycleIdStr.length > 24 || /[\s\-_]/.test(cycleIdStr)) {
        return cycleIdStr;
      }
    }

    // If cycles are still loading, show fallback or loading state
    if (cyclesLoading) {
      return fallbackName || 'Loading...';
    }

    // If no cycles available, use fallback name
    if (!Array.isArray(cycles) || cycles.length === 0) {
      return fallbackName || 'N/A';
    }

    // Try to find the cycle by comparing IDs as strings
    const cycle = cycles.find((c) => {
      const cId = String(c.id || c._id || '').trim();
      return cId === cycleIdStr;
    });

    // Return cycle name if found, otherwise use fallback, but never return the raw ID
    if (cycle) {
      return cycle.cycleName || cycle.name || fallbackName || 'N/A';
    }

    // If cycle not found and it's an ObjectId, don't show the ID - show N/A or fallback
    if (isObjectId) {
      return fallbackName || 'N/A';
    }

    // If it's not an ObjectId and not found in cycles, return it (might be a name)
    return cycleIdStr || fallbackName || 'N/A';
  };

  // Helper: render rating stars (up to 5)
  const renderRatingStars = (rating, maxStars = 5) => {
    const numericRating = Number(rating);
    if (!Number.isFinite(numericRating) || numericRating < 0) {
      return null;
    }

    const safeRating = Math.min(maxStars, numericRating);
    const filledStars = Math.floor(safeRating);

    return (
      <div className="flex items-center space-x-2">
        <div className="flex">
          {Array.from({ length: maxStars }).map((_, index) => (
            <Star
              key={index}
              className={`h-4 w-4 ${index < filledStars ? 'text-yellow-400' : 'text-gray-300'}`}
              fill={index < filledStars ? 'currentColor' : 'none'}
            />
          ))}
        </div>
        <span className="text-sm text-gray-900">{numericRating}</span>
      </div>
    );
  };

  // Render Reviews Tab
  const renderReviews = () => {
    if (profileLoading) {
      return (
        <div className="text-center py-12">
          <RefreshCw className="mx-auto h-12 w-12 text-gray-400 animate-spin" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Loading profile...</h3>
        </div>
      );
    }

    if (reviewsLoading) {
      return (
        <div className="text-center py-12">
          <RefreshCw className="mx-auto h-12 w-12 text-gray-400 animate-spin" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Loading reviews...</h3>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {reviews.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Star className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Reviews Found</h3>
            <p className="text-gray-600">You don't have any performance reviews yet.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Manager
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Progress
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rating
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Given At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reviews.map((review) => (
                    <tr key={review.id || review._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-gray-900 capitalize">
                            {review.manager || 'N/A'}
                          </div>
                          {review.managerEmail && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              {review.managerEmail}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {((typeof review.progressPercentage === 'number') || (review.totalGoals && review.totalGoals > 0)) ? (
                          <div className="flex items-center">
                            <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full"
                                style={{
                                  width: `${review.totalGoals > 0
                                    ? (review.goalsCompleted / review.totalGoals) * 100
                                    : (typeof review.progressPercentage === 'number' ? review.progressPercentage : 0)
                                    }%`,
                                }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-900">
                              {review.totalGoals > 0
                                ? `${review.goalsCompleted || 0}/${review.totalGoals}`
                                : `${typeof review.progressPercentage === 'number' ? review.progressPercentage : 0}%`}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderRatingStars(review.rating) || (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {review.createdAt
                          ? formatDate(review.createdAt, { includeTime: true })
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setViewingReview(review);
                              setShowReviewModal(true);
                            }}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {reviewsTotal > reviewsItemsPerPage && (
          <PaginationControls
            pagination={{
              currentPage: reviewsCurrentPage,
              totalPages: reviewsTotalPages,
              totalCount: reviewsTotal,
              pageSize: reviewsItemsPerPage
            }}
            onPageChange={(page) => {
              setReviewsCurrentPage(page);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onLimitChange={(limit) => {
              setReviewsItemsPerPage(limit);
              setReviewsCurrentPage(1);
            }}
          />
        )}
      </div>
    );
  };

  // Goal Modal
  const renderGoalModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4 !mt-0">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-900">
            {isViewMode ? 'View Goal' : editingGoal ? 'Edit Goal Progress' : 'Add New Goal'}
          </h3>
          <button
            onClick={() => {
              setShowGoalModal(false);
              setEditingGoal(null);
              setViewingGoal(null);
              setIsViewMode(false);
              setNewGoal(getInitialGoal());
              setFormErrors({});
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {isViewMode && viewingGoal ? (
          // View Mode - Card/Box display
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 space-y-6">
            <div className="border-b border-gray-200 pb-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-1 capitalize break-words">{viewingGoal.title}</h4>
              <p className="text-sm text-gray-600 break-words whitespace-pre-line">{viewingGoal.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Cycle</p>
                <p className="text-sm font-medium text-gray-900 capitalize break-words">
                  {getCycleName(viewingGoal.cycleId || viewingGoal.cycle, viewingGoal.cycleName)}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Priority</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize break-words ${getPriorityColor(viewingGoal.priority)}`}>
                  {viewingGoal.priority}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Start Date</p>
                <p className="text-sm font-medium text-gray-900 break-words">
                  {viewingGoal.startDate ? formatDate(viewingGoal.startDate, { includeTime: false }) : 'N/A'}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">End Date</p>
                <p className="text-sm font-medium text-gray-900 break-words">
                  {viewingGoal.endDate ? formatDate(viewingGoal.endDate, { includeTime: false }) : 'N/A'}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <p className="text-xs font-medium text-gray-500 uppercase mb-2">Progress</p>
              <div className="flex items-center space-x-3">
                <div className="flex-1 bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full transition-all"
                    style={{ width: `${viewingGoal.progress || 0}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium text-gray-900 min-w-[50px] text-right break-words">{viewingGoal.progress || 0}%</span>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <p className="text-xs font-medium text-gray-500 uppercase mb-2">Status</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium break-words ${getStatusColor(viewingGoal.status)}`}>
                {getStatusIcon(viewingGoal.status)}
                <span className="ml-1 capitalize break-words">{viewingGoal.status?.replace('_', ' ')}</span>
              </span>
            </div>

            {viewingGoal.remark && (
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Remark</p>
                <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                  {viewingGoal.remark}
                </p>
              </div>
            )}

            {viewingGoal.status === 'completed' && (
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Completed Date</p>
                <p className="text-sm font-medium text-gray-900 break-words">
                  {viewingGoal.completedAt
                    ? formatDate(viewingGoal.completedAt, { includeTime: false })
                    : viewingGoal.updatedAt
                      ? formatDate(viewingGoal.updatedAt, { includeTime: false })
                      : 'N/A'}
                </p>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowGoalModal(false);
                  setViewingGoal(null);
                  setIsViewMode(false);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmitGoal} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Goal Title {!editingGoal && <span className="text-red-500">*</span>}
              </label>
              {editingGoal ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900">
                  {newGoal.title}
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={newGoal.title}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 80);
                      setNewGoal({ ...newGoal, title: value });
                      if (formErrors.title) setFormErrors({ ...formErrors, title: undefined });
                    }}
                    onKeyPress={handleTextKeyPress}
                    maxLength={80}
                    required
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.title ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter goal title"
                  />
                  <p className="text-xs text-gray-500 mt-1">{newGoal.title.length}/80 characters</p>
                  {formErrors.title && (
                    <p className="text-sm text-red-600 mt-1">{formErrors.title}</p>
                  )}
                </>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description {!editingGoal && <span className="text-red-500">*</span>}
              </label>
              {editingGoal ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900 min-h-[80px]">
                  {newGoal.description}
                </div>
              ) : (
                <>
                  <textarea
                    value={newGoal.description}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 200);
                      setNewGoal({ ...newGoal, description: value });
                      if (formErrors.description) setFormErrors({ ...formErrors, description: undefined });
                    }}
                    onKeyPress={handleTextKeyPress}
                    maxLength={200}
                    required
                    rows="3"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.description ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter goal description"
                  />
                  <p className="text-xs text-gray-500 mt-1">{newGoal.description.length}/200 characters</p>
                  {formErrors.description && (
                    <p className="text-sm text-red-600 mt-1">{formErrors.description}</p>
                  )}
                </>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remark
              </label>
              <textarea
                value={newGoal.remark}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 1000);
                  setNewGoal({ ...newGoal, remark: value });
                  if (formErrors.remark) setFormErrors({ ...formErrors, remark: undefined });
                }}
                rows="3"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.remark ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="Add any additional notes for your manager (optional)"
              />
              <p className="text-xs text-gray-500 mt-1">{newGoal.remark.length}/1000 characters</p>
              {formErrors.remark && (
                <p className="text-sm text-red-600 mt-1">{formErrors.remark}</p>
              )}
            </div>

            {!editingGoal && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={newGoal.startDate}
                    onChange={(e) => {
                      setNewGoal({ ...newGoal, startDate: e.target.value });
                      if (formErrors.startDate) setFormErrors({ ...formErrors, startDate: undefined });
                    }}
                    min={new Date().toISOString().split('T')[0]}
                    required
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.startDate ? 'border-red-300' : 'border-gray-300'
                      }`}
                  />
                  {formErrors.startDate && (
                    <p className="text-sm text-red-600 mt-1">{formErrors.startDate}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={newGoal.endDate}
                    onChange={(e) => {
                      setNewGoal({ ...newGoal, endDate: e.target.value });
                      if (formErrors.endDate) setFormErrors({ ...formErrors, endDate: undefined });
                    }}
                    min={newGoal.startDate || new Date().toISOString().split('T')[0]}
                    required
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.endDate ? 'border-red-300' : 'border-gray-300'
                      }`}
                  />
                  {formErrors.endDate && (
                    <p className="text-sm text-red-600 mt-1">{formErrors.endDate}</p>
                  )}
                </div>
              </div>
            )}

            {editingGoal && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cycle</label>
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900">
                    {getCycleName(newGoal.cycle)}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(newGoal.priority)}`}>
                      {newGoal.priority}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {!editingGoal && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={newGoal.priority}
                    onChange={(e) => setNewGoal({ ...newGoal, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Progress (%)</label>
                <div className="flex items-center space-x-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={Number.isFinite(newGoal.progress) ? newGoal.progress : 0}
                    onChange={(e) => {
                      const v = Math.max(0, Math.min(100, Number(e.target.value || 0)));
                      // Automatically calculate status based on progress and endDate
                      const calculatedStatus = calculateStatus(v, newGoal.endDate);
                      setNewGoal({ ...newGoal, progress: v, status: calculatedStatus });
                      if (formErrors.progress) setFormErrors({ ...formErrors, progress: undefined });
                    }}
                    className="w-full"
                  />
                  <span className="w-10 text-right text-sm text-gray-700">{Number(newGoal.progress || 0)}%</span>
                </div>
                {formErrors.progress && (
                  <p className="text-sm text-red-600 mt-1">{formErrors.progress}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(newGoal.status)}`}>
                  {getStatusIcon(newGoal.status)}
                  <span className="ml-1 capitalize">{newGoal.status?.replace('_', ' ')}</span>
                </span>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowGoalModal(false);
                  setEditingGoal(null);
                  setViewingGoal(null);
                  setIsViewMode(false);
                  setNewGoal(getInitialGoal());
                  setFormErrors({});
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createGoalMutation.isPending || updateProgressMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createGoalMutation.isPending || updateProgressMutation.isPending
                  ? 'Saving...'
                  : editingGoal
                    ? 'Update Progress'
                    : 'Create Goal'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  const renderReviewModal = () => {
    if (!viewingReview) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4 !mt-0">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <h2 className="text-lg font-semibold text-gray-900">Review Details</h2>
            <button
              onClick={() => {
                setShowReviewModal(false);
                setViewingReview(null);
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Manager Information */}
            <div className="pb-4 border-b border-gray-200">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Manager
              </label>
              <p className="text-base font-semibold text-gray-900">
                {viewingReview.manager || viewingReview.managerName || 'N/A'}
              </p>
              {viewingReview.managerEmail && (
                <p className="text-sm text-gray-600 mt-1">{viewingReview.managerEmail}</p>
              )}
            </div>

            {/* Rating and Goals */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                <span className="text-lg font-semibold text-gray-900">
                  Rating: {viewingReview.rating !== undefined && viewingReview.rating !== null
                    ? viewingReview.rating
                    : 'N/A'}
                </span>
              </div>
              {viewingReview.totalGoals && viewingReview.totalGoals > 0 && (
                <div className="text-sm text-gray-600">
                  {viewingReview.goalsCompleted || 0} / {viewingReview.totalGoals} Goals
                </div>
              )}
            </div>

            {/* Progress Bar */}
            {viewingReview.totalGoals && viewingReview.totalGoals > 0 && (
              <div className="pb-4 border-b border-gray-200">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Progress
                </label>
                <div className="flex items-center space-x-3">
                  <div className="flex-1 bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full transition-all"
                      style={{
                        width: `${((viewingReview.goalsCompleted || 0) / viewingReview.totalGoals) * 100}%`,
                      }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900 min-w-[60px] text-right">
                    {viewingReview.goalsCompleted || 0} / {viewingReview.totalGoals}
                  </span>
                </div>
              </div>
            )}

            {/* Created At */}
            <div className="pb-4 border-b border-gray-200">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Given At
              </label>
              <p className="text-base text-gray-900">
                {viewingReview.createdAt
                  ? formatDate(viewingReview.createdAt, { includeTime: true })
                  : 'N/A'}
              </p>
            </div>

            {/* Last Updated At */}
            {viewingReview.updatedAt && (
              <div className="pb-4 border-b border-gray-200">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Last Updated At
                </label>
                <p className="text-base text-gray-900">
                  {formatDate(viewingReview.updatedAt, { includeTime: true })}
                </p>
              </div>
            )}

            {/* Comments */}
            {viewingReview.comments && (
              <div className="pb-4 border-gray-200">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Comments
                </label>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{viewingReview.comments}</p>
                </div>
              </div>
            )}

            {/* Reviewer Comments */}
            {viewingReview.reviewerComments && (
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Reviewer Comments
                </label>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{viewingReview.reviewerComments}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end p-4 border-t border-gray-200 sticky bottom-0 bg-white">
            <button
              onClick={() => {
                setShowReviewModal(false);
                setViewingReview(null);
              }}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderOutcomes = () => {
    if (profileLoading) {
      return (
        <div className="text-center py-12">
          <RefreshCw className="mx-auto h-12 w-12 text-gray-400 animate-spin" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Loading profile...</h3>
        </div>
      );
    }

    if (outcomesLoading) {
      return (
        <div className="text-center py-12">
          <RefreshCw className="mx-auto h-12 w-12 text-gray-400 animate-spin" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Loading outcomes...</h3>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {outcomes.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Outcomes Found</h3>
            <p className="text-gray-600">You don't have any performance outcomes yet.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Effective Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {outcomes.map((outcome) => {
                    const getOutcomeTypeDisplay = (type) => {
                      switch (type) {
                        case 'increment':
                          return { label: '💰 Salary Increment', color: 'text-green-600 bg-green-50' };
                        case 'promotion':
                          return { label: '🎯 Promotion', color: 'text-blue-600 bg-blue-50' };
                        case 'pip':
                          return { label: '📋 Performance Improvement Plan', color: 'text-yellow-600 bg-yellow-50' };
                        default:
                          return { label: type, color: 'text-gray-600 bg-gray-50' };
                      }
                    };

                    const typeDisplay = getOutcomeTypeDisplay(outcome.outcomeType);
                    let detailsText = '';

                    if (outcome.outcomeType === 'increment') {
                      const newSalary = outcome.outcomeDetails?.newSalary || outcome.increment?.newSalary || outcome.increment?.amount || 'N/A';
                      const percentage = outcome.outcomeDetails?.incrementPercentage || outcome.increment?.percentage || 'N/A';
                      detailsText = `New Salary: ₹${typeof newSalary === 'number' ? newSalary.toLocaleString('en-IN') : newSalary} (${typeof percentage === 'number' ? percentage.toFixed(2) : percentage}%)`;
                    } else if (outcome.outcomeType === 'promotion') {
                      const newRole = outcome.outcomeDetails?.newRole || outcome.outcomeDetails?.newPosition || outcome.promotion?.toRole || 'N/A';
                      const formattedRole = formatRoleLabel(newRole) || 'N/A';
                      detailsText = `New Position: ${formattedRole}`;
                    } else if (outcome.outcomeType === 'pip') {
                      const duration = outcome.pip?.duration || 'N/A';
                      detailsText = `Duration: ${duration} months`;
                    }

                    return (
                      <tr key={outcome.id || outcome._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeDisplay.color}`}>
                            {typeDisplay.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {detailsText || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {outcome.effectiveDate
                            ? formatDate(outcome.effectiveDate, { includeTime: false })
                            : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => {
                              setViewingOutcome(outcome);
                              setShowOutcomeModal(true);
                            }}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {outcomesTotal > outcomesItemsPerPage && (
          <PaginationControls
            pagination={{
              currentPage: outcomesCurrentPage,
              totalPages: outcomesTotalPages,
              totalCount: outcomesTotal,
              pageSize: outcomesItemsPerPage
            }}
            onPageChange={(page) => {
              setOutcomesCurrentPage(page);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onLimitChange={(limit) => {
              setOutcomesItemsPerPage(limit);
              setOutcomesCurrentPage(1);
            }}
          />
        )}
      </div>
    );
  };

  const renderOutcomeModal = () => {
    if (!viewingOutcome) return null;

    const o = viewingOutcome || {};
    const type = o.outcomeType || o.type || 'N/A';
    const effectiveDate = o.effectiveDate ? formatDate(o.effectiveDate, { includeTime: false }) : 'N/A';
    const currentSalary = o.outcomeDetails?.currentSalary ?? o.increment?.currentSalary ?? o.employee?.salaryInfo?.netSalary ?? o.employee?.salaryInfo?.basicSalary;
    const newSalary = o.outcomeDetails?.newSalary ?? o.increment?.newSalary ?? o.increment?.amount;
    const pctRaw = o.outcomeDetails?.incrementPercentage ?? o.increment?.percentage;
    const pct = pctRaw ?? (currentSalary && newSalary ? ((Number(newSalary) - Number(currentSalary)) / Number(currentSalary)) * 100 : undefined);
    const oldPosition = o.outcomeDetails?.oldPosition || o.outcomeDetails?.oldRole || o.promotion?.fromRole;
    const newPosition = o.outcomeDetails?.newPosition || o.outcomeDetails?.newRole || o.promotion?.toRole;
    const justification = typeof o.justification === 'string' ? o.justification : (o.justification?.overall || '');

    // Map role values to labels for display
    const availableRoles = [
      { value: 'super_admin', label: 'Super Admin' },
      { value: 'hr_manager', label: 'HR Manager' },
      { value: 'hr_executive', label: 'HR Executive' },
      { value: 'employee', label: 'Employee' },
      { value: 'customer', label: 'Customer' },
      { value: 'sales_manager', label: 'Sales Manager' },
      { value: 'sales_executive', label: 'Sales Executive' },
      { value: 'business_relationship_manager', label: 'Business Relationship Manager' },
      { value: 'business_development_executive', label: 'Business Development Executive' },
      { value: 'dispatch_manager', label: 'Dispatch Manager' },
      { value: 'support_manager', label: 'Support Manager' }
    ];

    const getRoleLabel = (roleValue) => {
      if (!roleValue) return roleValue;
      const role = availableRoles.find(r => r.value === roleValue);
      return role ? role.label : roleValue;
    };

    const displayOldPosition = oldPosition ? getRoleLabel(oldPosition) : oldPosition;
    const displayNewPosition = newPosition ? getRoleLabel(newPosition) : newPosition;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">View Outcome Details</h3>
            <button
              onClick={() => {
                setShowOutcomeModal(false);
                setViewingOutcome(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500">Outcome Type</p>
                <p className="font-medium text-gray-900 capitalize">{type}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Effective Date</p>
                <p className="font-medium text-gray-900">{effectiveDate}</p>
              </div>

              {type === 'increment' && (
                <>
                  <div>
                    <p className="text-sm text-gray-500">Current Salary</p>
                    <p className="font-medium text-gray-900">{currentSalary ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(currentSalary) : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">New Salary</p>
                    <p className="font-medium text-gray-900">{newSalary ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(newSalary) : 'N/A'}</p>
                  </div>
                  {pct !== undefined && !Number.isNaN(pct) && (
                    <div>
                      <p className="text-sm text-gray-500">Increment</p>
                      <p className="font-medium text-gray-900">{Number(pct).toFixed(1).replace(/\.0$/, '')}%</p>
                    </div>
                  )}
                </>
              )}

              {type === 'promotion' && (
                <>
                  <div>
                    <p className="text-sm text-gray-500">Old Position</p>
                    <p className="font-medium text-gray-900">{displayOldPosition || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">New Position</p>
                    <p className="font-medium text-gray-900">{displayNewPosition || 'N/A'}</p>
                  </div>
                </>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Justification</p>
                <p className="text-gray-900">{justification || 'N/A'}</p>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
            <button
              onClick={() => {
                setShowOutcomeModal(false);
                setViewingOutcome(null);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'goals', label: 'Goals', icon: Target },
    { id: 'reviews', label: 'Reviews', icon: Star },
    { id: 'outcomes', label: 'Outcomes', icon: Award }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Performance</h1>
            <p className="text-gray-600 mt-1">Track your goals</p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-blue-300'
                      }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        {activeTab === 'goals' && renderGoals()}
        {activeTab === 'reviews' && renderReviews()}
        {activeTab === 'outcomes' && renderOutcomes()}

        {/* Modals */}
        {showGoalModal && renderGoalModal()}
        {showReviewModal && viewingReview && renderReviewModal()}
        {showOutcomeModal && viewingOutcome && renderOutcomeModal()}
      </div>
    </div>
  );
};

export default MyPerformance;
